import React, { useEffect, useRef } from 'react';

/**
 * Reusable Success Popup Component
 * 
 * @param {boolean} show - Whether to show the popup
 * @param {function} onClose - Callback function when popup is closed
 * @param {string} title - Title text (default: "Success!")
 * @param {string} message - Main message text
 * @param {string} buttonText - Text for the OK button (default: "OK")
 * @param {number} autoCloseDelay - Auto-close delay in milliseconds (default: 5000, set to 0 to disable)
 */
const SuccessPopup = ({ 
  show, 
  onClose, 
  title = "Success!", 
  message, 
  buttonText = "OK",
  autoCloseDelay = 5000 
}) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (show && autoCloseDelay > 0) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Set new timeout to close after specified delay
      timeoutRef.current = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
    }
    // Cleanup timeout on unmount or when show changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, autoCloseDelay, onClose]);

  const handleDismiss = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    onClose();
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={handleDismiss}
      >
        <div 
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDismiss}
            style={{
              position: 'absolute',
              top: '0.75rem',
              right: '0.75rem',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: '#666',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            aria-label="Dismiss"
          >
            ×
          </button>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div style={{ 
              fontSize: '3rem', 
              marginBottom: '1rem',
              color: '#27ae60'
            }}>
              ✓
            </div>
            <h3 style={{ 
              margin: '0 0 0.5rem 0', 
              color: '#2c3e50',
              fontSize: '1.5rem'
            }}>
              {title}
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#666',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {message}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleDismiss}
              style={{
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#229954'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#27ae60'}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessPopup;
